import * as THREE from 'three';
import {
  FOG_DENSITY,
  AMBIENT_INTENSITY,
  SUN_INTENSITY,
  SHADOW_MAP_SIZE,
  SHADOW_AREA,
  CITY_EXTENT,
  FOREST_OUTER_RADIUS,
} from '../config';

export class EnvironmentSetup {
  sun: THREE.DirectionalLight;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // ─── Fog ───
    scene.fog = new THREE.FogExp2(0x88aacc, FOG_DENSITY);
    scene.background = new THREE.Color(0x88aacc);

    // ─── Ambient light ───
    const ambient = new THREE.AmbientLight(0xffffff, AMBIENT_INTENSITY);
    scene.add(ambient);

    // ─── Hemisphere light ───
    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x556b2f, 0.4);
    scene.add(hemi);

    // ─── Sun (directional) ───
    this.sun = new THREE.DirectionalLight(0xfff4e6, SUN_INTENSITY);
    this.sun.position.set(30, 50, 20);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 100;
    this.sun.shadow.camera.left = -SHADOW_AREA;
    this.sun.shadow.camera.right = SHADOW_AREA;
    this.sun.shadow.camera.top = SHADOW_AREA;
    this.sun.shadow.camera.bottom = -SHADOW_AREA;
    this.sun.shadow.bias = -0.001;
    scene.add(this.sun);
    scene.add(this.sun.target);

    // ─── Sky sphere ───
    this.createSky();

    // ─── Ground planes ───
    this.createGround();
  }

  private createSky() {
    const skyGeo = new THREE.SphereGeometry(FOREST_OUTER_RADIUS * 1.5, 32, 15);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color(0x0077ff) },
        bottomColor: { value: new THREE.Color(0xb0d4f1) },
        offset: { value: 20 },
        exponent: { value: 0.6 },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPos.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(sky);
  }

  private createGround() {
    // City ground (gray)
    const cityGround = new THREE.Mesh(
      new THREE.PlaneGeometry(CITY_EXTENT * 2 + 2, CITY_EXTENT * 2 + 2),
      new THREE.MeshLambertMaterial({ color: 0x666666 })
    );
    cityGround.rotation.x = -Math.PI / 2;
    cityGround.position.y = -0.01;
    cityGround.receiveShadow = true;
    this.scene.add(cityGround);

    // Forest ground (green circle)
    const forestGround = new THREE.Mesh(
      new THREE.CircleGeometry(FOREST_OUTER_RADIUS + 5, 64),
      new THREE.MeshLambertMaterial({ color: 0x4a7a3d })
    );
    forestGround.rotation.x = -Math.PI / 2;
    forestGround.position.y = -0.02;
    forestGround.receiveShadow = true;
    this.scene.add(forestGround);
  }

  // Move shadow camera to follow player
  updateShadowTarget(x: number, z: number) {
    this.sun.position.set(x + 30, 50, z + 20);
    this.sun.target.position.set(x, 0, z);
    this.sun.target.updateMatrixWorld();
  }
}
